import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import './BikePackage.css';
import './CarPackage.css';
import Nav from "../Nav";
import Footer from "../Footer";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Seo from "../../Seo/Seo";
import { useSeoMeta } from "../../Seo/useSeoMeta";
import { SITE_NAME } from "../../Seo/seoConfig";

const CarPackage = () => {
  const centralizedSeo = useSeoMeta();
  const [carPackages, setCarPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  // { index, start, visible } of the hovered card and the slider "page" it is
  // on, or null when nothing is hovered.
  const [active, setActive] = useState(null);
  const sliderRef = useRef(null);
  const trackRef = useRef(null);

  // The hovered card expands (flex 0 -> flex 1) and only the other cards
  // currently on screen shrink to make room, so the slider's total width and
  // scroll position never change while hovering.
  const activate = (index) => {
    const track = trackRef.current;
    if (!track || !window.matchMedia("(hover: hover) and (min-width: 577px)").matches) return;
    const visible = Math.round(
      parseFloat(getComputedStyle(sliderRef.current).getPropertyValue("--visible")) || 5
    );
    const cardWidth = track.clientWidth / visible;
    let start = Math.round(track.scrollLeft / cardWidth);
    start = Math.min(Math.max(start, index - visible + 1), index);
    setActive({ index, start, visible });
  };

  const cardStyle = (index) => {
    if (!active || index < active.start || index >= active.start + active.visible) return undefined;
    const grown = active.visible >= 5 ? 2.5 : 2;
    const factor = index === active.index ? grown : (active.visible - grown) / (active.visible - 1);
    return { flexBasis: `calc(var(--card-width) * ${factor})` };
  };

  const cardClassName = (index) => {
    if (!active) return "car-slider__card";
    if (index === active.index) return "car-slider__card is-active";
    return "car-slider__card is-dimmed";
  };

  // Shows/hides the arrows depending on whether there is more to scroll to.
  const updateArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanScrollPrev(track.scrollLeft > 1);
    setCanScrollNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 1);
  }, []);

  const scrollByCard = (direction) => {
    const track = trackRef.current;
    const card = track?.querySelector(".car-slider__card");
    if (!card) return;
    track.scrollBy({ left: direction * card.offsetWidth, behavior: "smooth" });
  };

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}AllPackages`);
        const result = await response.json();

        const allPackages = result?.data || [];
        // Packages without an image would render as an empty black card in
        // the slider, so they're left out until an image is added in admin.
        const filteredCars = allPackages.filter(pkg =>
          pkg?.category?.name?.toLowerCase().includes("cars tour") &&
          pkg?.image?.[0]
        );

        setCarPackages(filteredCars || []);
      } catch (error) {
        console.error("Error fetching car packages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  useEffect(() => {
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [carPackages, updateArrows]);
  return (
    <div className="container-fluid p-0">
      <Seo
        title={`Ladakh Car Tour Packages | ${SITE_NAME}`}
        description={`Compare Ladakh car tour packages with ${SITE_NAME} - comfortable travel options with itineraries for your Leh Ladakh trip.`}
        centralizedSeo={centralizedSeo}
        type="website"
      />
      <Nav />

      <section className="w-100 car-slider" ref={sliderRef}>
        <h1 className='d-none'>Best Car Packages</h1>
        <button
          type="button"
          className="car-slider__arrow car-slider__arrow--prev"
          onClick={() => scrollByCard(-1)}
          disabled={!canScrollPrev}
          aria-label="Previous car packages"
        >
          <ChevronLeft size={24} />
        </button>
        <div
          className="car-slider__track"
          ref={trackRef}
          onScroll={updateArrows}
          onMouseLeave={() => setActive(null)}
        >
          {!loading && carPackages.length === 0 ? (
            <p className="car-slider__empty">No Cars Tour Packages Found</p>
          ) : (
            carPackages.map((pkg, index) => (
              <Link
                key={pkg?.id}
                to={`/detail-car-package/${pkg?.name?.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
                className={cardClassName(index)}
                style={cardStyle(index)}
                onMouseEnter={() => activate(index)}
                onFocus={() => activate(index)}
                onBlur={() => setActive(null)}
              >
                <img
                  src={pkg?.image?.[0] || "/placeholder.svg?height=300&width=400"}
                  alt={pkg?.name || `Tour ${index + 1}`}
                  loading={index < 5 ? "eager" : "lazy"}
                />
                <div className="car-slider__content">
                  <p className="car-slider__index">{String(index + 1).padStart(2, "0")}</p>
                  <h2 className="car-slider__title">{pkg?.name}</h2>
                  <div className="car-slider__details">
                    <small className="car-slider__location">{pkg?.location}</small>
                    <span className="car-slider__cta">View Package</span>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
        <button
          type="button"
          className="car-slider__arrow car-slider__arrow--next"
          onClick={() => scrollByCard(1)}
          disabled={!canScrollNext}
          aria-label="Next car packages"
        >
          <ChevronRight size={24} />
        </button>
      </section>


      {/* <div className="position-fixed bottom-0 end-0 m-4">
        <button
          className="btn btn-light rounded-circle shadow"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
            <path d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" />
          </svg>
        </button>
      </div> */}

      <Footer />
    </div>
  );
};

export default CarPackage;
